import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Shield, Eye, Wind, Flame, AlertTriangle, ChevronDown, ChevronUp,
  ArrowLeft, CheckCircle, X
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

const ICON_MAP = {
  eye: Eye,
  wind: Wind,
  flame: Flame,
  shield: Shield,
  'alert-triangle': AlertTriangle,
};

const SECTION_COLORS = {
  eye_protection:    { accent: 'text-blue-400',   border: 'border-blue-500/40',   bg: 'bg-blue-500/10' },
  ventilation:       { accent: 'text-cyan-400',    border: 'border-cyan-500/40',   bg: 'bg-cyan-500/10' },
  fire_safety:       { accent: 'text-orange-400',  border: 'border-orange-500/40', bg: 'bg-orange-500/10' },
  enclosure:         { accent: 'text-green-400',   border: 'border-green-500/40',  bg: 'bg-green-500/10' },
  materials_safety:  { accent: 'text-red-400',     border: 'border-red-500/40',    bg: 'bg-red-500/10' },
};

const LaserSafety = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/laser/safety`)
      .then(res => {
        setSections(res.data || []);
        // Auto-expand first section
        if (res.data?.length > 0) setExpanded({ [res.data[0].id]: true });
      })
      .catch(err => console.error('Failed to load laser safety guide:', err))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.LASER_SAFETY)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Shield className="w-12 h-12 text-red-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading safety guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="laser-safety" style={bgStyle(IMAGES.LASER_SAFETY)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <Link to="/laser" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-red-500/20 rounded-lg">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-red-500 mb-1">SAFETY GUIDE</div>
            <h1 className="text-3xl md:text-4xl font-bold text-red-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Laser Safety
            </h1>
            <p className="text-sm text-zinc-300 mt-1">
              Eye protection · Ventilation · Fire safety · Enclosures · Toxic materials
            </p>
          </div>
        </div>
      </div>

      {/* Critical banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-red-900/40 border border-red-500/60 mb-6">
        <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-red-300 font-bold text-sm mb-1">Read This Before Operating Any Laser</p>
          <p className="text-red-200 text-sm">
            Lasers cause instant, irreversible eye damage. Toxic fumes from incorrect materials
            can injure or kill. Fires can escalate in seconds without supervision.
            This guide covers the minimum safety requirements for laser operation.
          </p>
        </div>
      </div>

      <div className="space-y-4 max-w-4xl">
        {sections.map(section => {
          const colors = SECTION_COLORS[section.id] || SECTION_COLORS.fire_safety;
          const Icon = ICON_MAP[section.icon] || Shield;
          const isOpen = expanded[section.id];

          return (
            <Card key={section.id} className={`bg-zinc-900 border-zinc-800 ${isOpen ? colors.border : ''}`}>
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => toggle(section.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <Icon className={`w-5 h-5 ${colors.accent}`} />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{section.title}</CardTitle>
                      <CardDescription className="text-zinc-400 text-sm mt-0.5">{section.summary}</CardDescription>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                           : <ChevronDown className="w-5 h-5 text-zinc-400 flex-shrink-0" />}
                </div>
              </CardHeader>

              {isOpen && (
                <CardContent className="space-y-6 pt-0">
                  {/* Content blocks */}
                  {section.content?.map((block, i) => (
                    <div key={i}>
                      <h4 className={`font-semibold mb-2 ${colors.accent}`}>{block.heading}</h4>
                      <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{block.body}</p>
                    </div>
                  ))}

                  {/* Warnings */}
                  {section.warnings?.length > 0 && (
                    <div className="p-4 rounded-xl bg-red-900/30 border border-red-500/40">
                      <h4 className="text-red-300 font-semibold text-sm mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Critical Warnings
                      </h4>
                      <ul className="space-y-2">
                        {section.warnings.map((w, i) => (
                          <li key={i} className="flex items-start gap-2 text-red-200 text-sm">
                            <X className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                            {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {sections.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Safety guide loading...</p>
          </div>
        )}
      </div>

      {/* Footer reminder */}
      <div className="max-w-4xl mt-8 p-4 rounded-xl bg-zinc-900 border border-zinc-700">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-semibold text-sm mb-1">Pre-Operation Safety Checklist</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-zinc-400">
              {['Eye protection on / enclosure closed', 'Extraction fan running',
                'Air assist compressor running', 'CO2 fire extinguisher within reach',
                'Material confirmed safe to laser', 'Machine will not be left unattended'].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-3 h-3 border border-zinc-600 rounded-sm flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaserSafety;
